import { createAsyncThunk } from "@reduxjs/toolkit";

import { type AppAccessType } from "@fitness-app/data-models/entities/Automation";
import {
  type TraineeStatus,
  type TraineeWithAssignedTrainerAndProduct,
} from "@fitness-app/data-models/entities/Trainee";

import { getPageRange } from "../../../helpers/getPageRange";
import { type AsyncThunkCreator } from "../../../index";
import { TRAINEES_REDUCER_NAME } from "../types";

type Payload = {
  page?: number;
  assignedTrainerId?: string | null;
  statuses?: TraineeStatus[] | null;
  accessType?: AppAccessType[] | null;
  tags?: string[] | null;
  ghost?: boolean | null;
} | void;

export const fetchTrainees = createAsyncThunk<
  {
    page: number;
    list: TraineeWithAssignedTrainerAndProduct[];
    totalPages: number;
    count: number | null;
    countSignature: string;
  },
  Payload,
  AsyncThunkCreator<string>
>(`${TRAINEES_REDUCER_NAME}/fetchTrainees`, async (payload, { rejectWithValue, extra: { db }, getState }) => {
  const { listSize, filters, searchTerm, countSignature, totalPages, page: loadedPage } = getState().trainees;

  const { start, stop, currentPage } = getPageRange(payload?.page, listSize);

  // count=exact re-runs the RLS predicate over every matching row, which on this
  // table is the dominant cost. Paging does not change the total, so reuse it
  // when only the page moved. `pending` has already folded the payload into
  // `filters`, so those plus the search term identify the result set. Re-fetching
  // the SAME page still counts — that is what runs after adding or removing a
  // client, and the total has to follow.
  const nextCountSignature = JSON.stringify({ filters, searchTerm, listSize });
  const reuseCount = countSignature === nextCountSignature && totalPages > 0 && currentPage !== loadedPage;

  let query = db
    .from("trainee")
    .select(
      "*, assignedTrainer:assignedTrainerId(id, firstName, lastName, userId, email), productClient:productId(product(name), productId)",
      reuseCount ? undefined : { count: "exact" },
    )
    .order("createdAt", { ascending: false });

  if (payload?.assignedTrainerId) {
    if (payload.assignedTrainerId === "none") {
      query = query.is("assignedTrainerId", null);
    } else {
      query = query.eq("assignedTrainerId", payload.assignedTrainerId);
    }
  } else if (filters.currentAssignedTrainerFilter) {
    query = query.eq("assignedTrainerId", filters.currentAssignedTrainerFilter);
  }

  if (payload?.statuses?.length) {
    query = query.in("status", payload.statuses);
  }

  if (typeof payload?.ghost === "boolean") {
    query = query.eq("ghost", payload.ghost);
  }

  if (payload?.tags?.length) {
    const tagsContains = payload.tags.filter((tag) => !tag.startsWith("WITHOUT__"));
    const tagsNotContains = payload.tags
      .filter((tag) => tag.startsWith("WITHOUT__"))
      .map((tag) => tag.replace("WITHOUT__", ""));

    if (tagsContains.length) {
      query = query.or(tagsContains.map((tag) => `tags.cs.["${tag}"]`).join(","));
    }

    if (tagsNotContains.length) {
      tagsNotContains.forEach((tag) => {
        query = query.not("tags", "cs", `["${tag}"]`);
      });
    }
  }

  if (payload?.accessType?.length) {
    query = query.in("type", payload.accessType);
  }

  // `undefined` = zawołano bez zdania na temat filtra, więc obowiązuje ten z kolumn.
  // `null` = jawne „bez filtra" — dotąd wpadało w tę samą gałąź i wyczyszczony
  // filtr działał dopiero od następnego pobrania, a wyszukiwanie po całej bazie
  // było przycinane do domyślnych statusów i typu dostępu.
  if (payload?.statuses === undefined && filters.currentStatusFilter?.length) {
    query = query.in("status", filters.currentStatusFilter);
  }

  if (!payload?.ghost && typeof filters.currentGhostFilter === "boolean") {
    query = query.eq("ghost", filters.currentGhostFilter);
  }

  if (!payload?.tags && filters.currentFilteredTags?.length) {
    query = query.or(filters.currentFilteredTags.map((tag) => `tags.cs.["${tag}"]`).join(","));
  }

  if (payload?.accessType === undefined && filters.currentAccessType?.length) {
    query = query.in("type", filters.currentAccessType);
  }

  if (searchTerm) {
    query = query.or(`email.ilike.%${searchTerm}%,lastName.ilike.%${searchTerm}%,firstName.ilike.%${searchTerm}%`);
  }

  const { error, data, count } = await query
    .range(start, stop)
    .limit(listSize)
    .returns<TraineeWithAssignedTrainerAndProduct[]>();

  if (error) {
    return rejectWithValue(error.message);
  }

  return {
    list: data,
    page: currentPage,
    totalPages: reuseCount ? totalPages : count ? Math.ceil(count / listSize) : 0,
    count,
    countSignature: nextCountSignature,
  };
});
